// frontend/src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import {
  MessageSquare, Star, Clock, List, UserPlus, UserCheck,
  Film, Activity, Users, Eye, BookmarkCheck, PauseCircle,
  XCircle, Zap, Flame
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import PostCard from '@/components/PostCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── STATUS CONFIG ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  plan_to_watch: { label: 'Planning to Watch', Icon: Clock,         color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20'   },
  watching:      { label: 'Watching',           Icon: Eye,           color: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/20'  },
  completed:     { label: 'Recently Watched',   Icon: BookmarkCheck, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
  on_hold:       { label: 'On Hold',            Icon: PauseCircle,   color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  dropped:       { label: 'Dropped',            Icon: XCircle,       color: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/20'    },
};

// ─── ACTIVITY TIMELINE ITEM — preserved exactly from your original ──────────────
function ActivityTimelineItem({ activity }) {
  const typeMap = {
    watching:  { label: 'Started watching', color: 'bg-green-500'  },
    watched:   { label: 'Watched',           color: 'bg-yellow-500' },
    added:     { label: 'Added to list',     color: 'bg-blue-500'   },
    commented: { label: 'Commented on',      color: 'bg-purple-500' },
  };
  const type = typeMap[activity.activity_type] || { label: activity.activity_type, color: 'bg-primary' };
  const link = activity.tmdb_id ? `/${activity.media_type || 'movie'}/${activity.tmdb_id}` : '#';

  return (
    <div className="flex items-start gap-4 group">
      <div className="flex flex-col items-center">
        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${type.color} flex-shrink-0`} />
        <div className="w-px flex-1 bg-white/5 mt-1 min-h-[32px]" />
      </div>
      <Link to={link} className="flex-1 pb-4">
        <div className="flex items-center gap-3 group-hover:text-primary transition-colors">
          {activity.poster_path && (
            <img src={`https://image.tmdb.org/t/p/w92${activity.poster_path}`} alt={activity.title}
              className="w-8 h-12 rounded object-cover border border-white/10 flex-shrink-0" />
          )}
          <div>
            <p className="text-sm text-gray-200">
              <span className="text-muted-foreground">{type.label} </span>
              <span className="font-semibold text-white group-hover:text-primary transition-colors">
                {activity.title || 'Unknown'}
              </span>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
      </Link>
    </div>
  );
}

// ─── NEW: TASTE MATCH BANNER — shows on other users' profiles ─────────────────
function TasteMatchBanner({ targetUserId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/taste-match/${targetUserId}`,
          { withCredentials: true }
        );
        setData(res.data);
      } catch { /* not logged in or no overlap */ }
      finally { setLoading(false); }
    };
    load();
  }, [targetUserId]);

  if (loading || !data || data.is_self || data.shared_count === 0) return null;

  return (
    <div className="flex items-center gap-4 bg-primary/8 border border-primary/20 rounded-2xl px-5 py-4 mt-2">
      <Flame className="w-5 h-5 text-primary flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white">
          You share <span className="text-primary">{data.shared_count} movies</span> in common
        </p>
        {data.shared_movies?.length > 0 && (
          <div className="flex gap-1.5 mt-2">
            {data.shared_movies.slice(0, 6).map((m, i) => (
              m.poster_path && (
                <img key={i}
                  src={`https://image.tmdb.org/t/p/w92${m.poster_path}`}
                  alt={m.title}
                  title={m.title}
                  className="w-8 h-12 rounded object-cover border border-white/10"
                />
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── NEW: WATCH STATUSES TAB ───────────────────────────────────────────────────
function WatchStatuses({ userId }) {
  const [statuses, setStatuses]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/watch-status/${userId}`);
        setStatuses(res.data || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [userId]);

  const counts = statuses.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});

  const filtered = activeFilter === 'all'
    ? statuses
    : statuses.filter(s => s.status === activeFilter);

  if (loading) return (
    <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-3">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="aspect-[2/3] rounded-xl skeleton-shimmer bg-muted/20" />
      ))}
    </div>
  );

  if (statuses.length === 0) return (
    <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
      <Eye className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
      <p className="text-muted-foreground">No watch statuses yet.</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
            activeFilter === 'all'
              ? 'bg-white/15 text-white border-white/30'
              : 'text-muted-foreground border-white/10 hover:border-white/20 hover:text-white'
          }`}
        >
          All ({statuses.length})
        </button>
        {Object.entries(STATUS_CONFIG).map(([key, { label, Icon, color, bg, border }]) => {
          if (!counts[key]) return null;
          return (
            <button key={key} onClick={() => setActiveFilter(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
                activeFilter === key
                  ? `${bg} ${color} ${border}`
                  : 'text-muted-foreground border-white/10 hover:border-white/20 hover:text-white'
              }`}>
              <Icon className="w-3 h-3" />
              {label} ({counts[key]})
            </button>
          );
        })}
      </div>

      {/* Poster grid */}
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-3">
        {filtered.map(item => {
          const cfg  = STATUS_CONFIG[item.status] || STATUS_CONFIG.watching;
          const Icon = cfg.Icon;
          return (
            <Link
              key={`${item.tmdb_id}-${item.status}`}
              to={`/${item.media_type || 'movie'}/${item.tmdb_id}`}
              className="group relative"
            >
              {item.poster_path ? (
                <img
                  src={`https://image.tmdb.org/t/p/w300${item.poster_path}`}
                  alt={item.title}
                  className="rounded-xl w-full aspect-[2/3] object-cover border border-white/5 transition-transform duration-300 group-hover:scale-105 group-hover:border-primary/50"
                />
              ) : (
                <div className="rounded-xl w-full aspect-[2/3] bg-muted flex items-center justify-center border border-white/5">
                  <Film className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
              {/* Status badge */}
              <div className={`absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-md ${cfg.bg} border ${cfg.border} backdrop-blur-sm`}>
                <Icon className={`w-2.5 h-2.5 ${cfg.color}`} />
              </div>
              {/* TV progress badge */}
              {item.media_type === 'tv' && item.season && (
                <div className="absolute top-1.5 right-1.5 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded backdrop-blur-sm">
                  S{item.season}{item.episode ? `E${item.episode}` : ''}
                </div>
              )}
              {/* Hover title */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-2 rounded-xl">
                <span className="text-white text-[10px] font-bold px-1 text-center leading-tight line-clamp-2">
                  {item.title}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ─── NEW: USER POSTS TAB ───────────────────────────────────────────────────────
function UserPosts({ userId, currentUser }) {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/posts/feed?page=1&limit=50`,
          { withCredentials: true }
        );
        setPosts((res.data.posts || []).filter(p => p.user_id === userId));
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [userId]);

  const handleDelete = (postId) => setPosts(prev => prev.filter(p => p.post_id !== postId));

  if (loading) return (
    <div className="space-y-4">
      {[1, 2].map(i => (
        <div key={i} className="bg-card border border-white/6 rounded-2xl p-5 space-y-3">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full skeleton-shimmer bg-muted/30" />
            <div className="space-y-2 flex-1">
              <div className="h-3.5 w-32 rounded skeleton-shimmer bg-muted/30" />
              <div className="h-3 w-20 rounded skeleton-shimmer bg-muted/30" />
            </div>
          </div>
          <div className="h-3.5 w-full rounded skeleton-shimmer bg-muted/30" />
          <div className="h-3.5 w-3/4 rounded skeleton-shimmer bg-muted/30" />
        </div>
      ))}
    </div>
  );

  if (posts.length === 0) return (
    <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
      <Zap className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
      <p className="text-muted-foreground">No posts yet.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {posts.map(post => (
        <PostCard
          key={post.post_id}
          post={post}
          currentUser={currentUser}
          onDelete={handleDelete}
        />
      ))}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// PROFILE PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function Profile() {
  const { id } = useParams();

  const [profile, setProfile]           = useState(null);
  const [currentUser, setCurrentUser]   = useState(null);
  const [loading, setLoading]           = useState(true);
  const [latestStatus, setLatestStatus] = useState(null);
  const [followStatus, setFollowStatus] = useState({ is_following: false, follower_count: 0, following_count: 0 });
  const [followLoading, setFollowLoading] = useState(false);
  const [activity, setActivity]         = useState([]);

  const isOwnProfile = !id || (currentUser && currentUser.user_id === id);

  // ── preserved exactly from your original ──────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const meRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setCurrentUser(meRes.data);
      } catch { /* not logged in */ }
    };
    init();
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const endpoint = id ? `${BACKEND_URL}/api/profile/${id}` : `${BACKEND_URL}/api/profile/me`;
      const res = await axios.get(endpoint, { withCredentials: true });
      setProfile(res.data);

      const targetId = res.data.user.user_id;

      try {
        const watchRes = await axios.get(`${BACKEND_URL}/api/watch-status/latest/${targetId}`);
        if (watchRes.data?.status) setLatestStatus(watchRes.data);
      } catch { /* no watch data */ }

      if (id) {
        try {
          const fsRes = await axios.get(`${BACKEND_URL}/api/follow/status/${targetId}`, { withCredentials: true });
          setFollowStatus(fsRes.data);
        } catch { /* not logged in or same user */ }
      }

      try {
        const actRes = await axios.get(`${BACKEND_URL}/api/activity/user/${targetId}?limit=30`);
        setActivity(actRes.data || []);
      } catch { /* no activity */ }

    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ── preserved exactly from your original ──────────────────
  const handleFollow = async () => {
    if (!profile) return;
    setFollowLoading(true);
    const targetId = profile.user.user_id;
    try {
      if (followStatus.is_following) {
        await axios.delete(`${BACKEND_URL}/api/follow/${targetId}`, { withCredentials: true });
        setFollowStatus(prev => ({ ...prev, is_following: false, follower_count: prev.follower_count - 1 }));
        toast.success('Unfollowed');
      } else {
        await axios.post(`${BACKEND_URL}/api/follow`, { target_user_id: targetId }, { withCredentials: true });
        setFollowStatus(prev => ({ ...prev, is_following: true, follower_count: prev.follower_count + 1 }));
        toast.success('Following!');
      }
    } catch (err) {
      if (err.response?.status === 401) toast.error('Please login to follow');
      else toast.error('Something went wrong');
    } finally {
      setFollowLoading(false);
    }
  };

  // ── Loading / not found — preserved exactly ───────────────
  if (loading) return (
    <div className="min-h-screen pt-24 px-8 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-6">
        <Skeleton className="h-28 w-28 rounded-full bg-muted/20" />
        <div className="space-y-3">
          <Skeleton className="h-8 w-48 bg-muted/20" />
          <Skeleton className="h-4 w-64 bg-muted/20" />
          <Skeleton className="h-4 w-32 bg-muted/20" />
        </div>
      </div>
      <Skeleton className="h-40 w-full bg-muted/20 rounded-2xl" />
    </div>
  );

  if (!profile) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <p className="text-xl text-muted-foreground">User not found</p>
      </div>
    </div>
  );

  const { user, threads, comments, movies = [] } = profile;

  // ── preserved exactly from your original ──────────────────
  const groupedLists = {};
  movies.forEach(m => {
    const listName = m.list_name || "Watchlist";
    if (!groupedLists[listName]) groupedLists[listName] = [];
    groupedLists[listName].push(m);
  });

  const recentPosters = movies.filter(m => m.poster_path).slice(0, 6);

  return (
    <div className="min-h-screen pt-20 pb-16">

      {/* ── PROFILE HEADER — preserved exactly ───────────── */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 flex">
          {recentPosters.map((m, i) => (
            <div key={i} className="flex-1 min-w-0 overflow-hidden" style={{ opacity: 0.15 + i * 0.05 }}>
              <img src={`https://image.tmdb.org/t/p/w500${m.poster_path}`} alt=""
                className="w-full h-full object-cover blur-sm scale-110" />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/90 to-background" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">

            {/* Avatar with watching indicator — preserved exactly */}
            <div className="relative flex-shrink-0">
              <img src={user.picture} alt={user.name}
                className="w-28 h-28 rounded-full border-4 border-card shadow-2xl" />
              {latestStatus && (
                <span className="absolute bottom-1 right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-background shadow-lg flex items-center justify-center">
                  <span className="animate-ping absolute w-full h-full rounded-full bg-green-400 opacity-75" />
                </span>
              )}
            </div>

            <div className="flex-1 text-center md:text-left space-y-4">
              <div>
                <div className="flex flex-wrap items-center gap-3 justify-center md:justify-start">
                  <h1 className="text-3xl font-heading font-bold">{user.name}</h1>
                  {latestStatus && STATUS_CONFIG[latestStatus.status] && (() => {
                    const cfg = STATUS_CONFIG[latestStatus.status];
                    const StatusIcon = cfg.Icon;
                    let label = `${cfg.label}: ${latestStatus.title}`;
                    if (latestStatus.media_type === 'tv' && latestStatus.season) {
                      label += ` S${latestStatus.season}`;
                      if (latestStatus.episode) label += ` E${latestStatus.episode}`;
                    }
                    return (
                      <span className={`flex items-center gap-1.5 text-xs font-semibold ${cfg.color} ${cfg.bg} border ${cfg.border} px-2.5 py-1 rounded-full`}>
                        <StatusIcon className="w-3 h-3" />
                        {label}
                      </span>
                    );
                  })()}
                </div>
                <p className="text-muted-foreground mt-1">{user.bio || "Movie enthusiast"}</p>
              </div>

              {/* Stats row — preserved exactly */}
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10">
                  <Star className="w-4 h-4 text-yellow-400" fill="currentColor" />
                  <span className="font-bold text-sm">{user.karma}</span>
                  <span className="text-xs text-muted-foreground">Karma</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10">
                  <Film className="w-4 h-4 text-blue-400" />
                  <span className="font-bold text-sm">{movies.length}</span>
                  <span className="text-xs text-muted-foreground">Saved</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10">
                  <Users className="w-4 h-4 text-purple-400" />
                  <span className="font-bold text-sm">{followStatus.follower_count}</span>
                  <span className="text-xs text-muted-foreground">Followers</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10">
                  <Users className="w-4 h-4 text-indigo-400" />
                  <span className="font-bold text-sm">{followStatus.following_count}</span>
                  <span className="text-xs text-muted-foreground">Following</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10">
                  <Clock className="w-4 h-4 text-green-400" />
                  <span className="text-xs text-muted-foreground">
                    Joined {formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>

              {/* Follow button — preserved exactly */}
              {!isOwnProfile && currentUser && (
                <button onClick={handleFollow} disabled={followLoading}
                  className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm transition-all active:scale-95 disabled:opacity-60 ${
                    followStatus.is_following
                      ? 'bg-white/10 text-white border border-white/20 hover:bg-red-500/20 hover:text-red-300 hover:border-red-500/30'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 btn-glow'
                  }`}>
                  {followStatus.is_following
                    ? <><UserCheck className="w-4 h-4" /> Following</>
                    : <><UserPlus className="w-4 h-4" /> Follow</>
                  }
                </button>
              )}

              {/* ── NEW: Taste match banner (other profiles only) ── */}
              {!isOwnProfile && id && (
                <TasteMatchBanner targetUserId={id} />
              )}
            </div>
          </div>

          {/* Currently Watching Card — replaced with latest status link */}
          {latestStatus && latestStatus.tmdb_id && (
            <div className="mt-8 max-w-sm">
              <Link to={`/${latestStatus.media_type || 'movie'}/${latestStatus.tmdb_id}`}
                className="flex items-center gap-4 bg-card/50 border border-white/5 rounded-2xl p-4 hover:border-primary/40 transition-colors group">
                {latestStatus.poster_path && (
                  <img
                    src={`https://image.tmdb.org/t/p/w92${latestStatus.poster_path}`}
                    alt={latestStatus.title}
                    className="w-12 h-18 rounded-lg object-cover border border-white/10"
                  />
                )}
                <div>
                  <p className="text-sm font-bold text-white group-hover:text-primary transition-colors">
                    {latestStatus.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {STATUS_CONFIG[latestStatus.status]?.label || latestStatus.status}
                    {latestStatus.media_type === 'tv' && latestStatus.season && (
                      <> · S{latestStatus.season}{latestStatus.episode ? `E${latestStatus.episode}` : ''}</>
                    )}
                  </p>
                </div>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ── CONTENT TABS ─────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
        <Tabs defaultValue="lists" className="w-full">
          <TabsList className="bg-white/5 h-auto mb-8 p-1 gap-1 flex-wrap">
            {/* existing tabs — preserved */}
            <TabsTrigger value="lists"
              className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <List className="w-4 h-4 mr-1.5" /> Lists ({Object.keys(groupedLists).length})
            </TabsTrigger>
            {/* ── NEW tabs ── */}
            <TabsTrigger value="tracking"
              className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Eye className="w-4 h-4 mr-1.5" /> Tracking
            </TabsTrigger>
            <TabsTrigger value="posts"
              className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Zap className="w-4 h-4 mr-1.5" /> Posts
            </TabsTrigger>
            {/* existing tabs — preserved */}
            <TabsTrigger value="activity"
              className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Activity className="w-4 h-4 mr-1.5" /> Activity ({activity.length})
            </TabsTrigger>
            <TabsTrigger value="threads"
              className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <MessageSquare className="w-4 h-4 mr-1.5" /> Threads ({threads.length})
            </TabsTrigger>
            <TabsTrigger value="comments"
              className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Comments ({comments.length})
            </TabsTrigger>
          </TabsList>

          {/* LISTS — preserved exactly from your original */}
          <TabsContent value="lists" className="space-y-10">
            {Object.keys(groupedLists).length === 0 && (
              <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
                <Film className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No lists yet.</p>
              </div>
            )}
            {Object.entries(groupedLists).map(([listName, items]) => (
              <div key={listName}>
                <div className="flex items-center gap-2 mb-4">
                  <List className="w-4 h-4 text-primary" />
                  <h3 className="text-lg font-bold">{listName}
                    <span className="text-muted-foreground text-sm font-normal ml-2">({items.length})</span>
                  </h3>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-3">
                  {items.map(movie => (
                    <Link key={`${movie.tmdb_id}-${movie.list_id}`} to={`/movie/${movie.tmdb_id}`} className="group relative">
                      {movie.poster_path ? (
                        <img src={`https://image.tmdb.org/t/p/w300${movie.poster_path}`} alt={movie.title}
                          className="rounded-xl w-full aspect-[2/3] object-cover border border-white/5 transition-transform duration-300 group-hover:scale-105 group-hover:border-primary/50" />
                      ) : (
                        <div className="rounded-xl w-full aspect-[2/3] bg-muted flex items-center justify-center border border-white/5">
                          <Film className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-2 rounded-xl">
                        <span className="text-white text-[10px] font-bold px-1 text-center leading-tight">{movie.title}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* ── NEW: TRACKING TAB ── */}
          <TabsContent value="tracking">
            <WatchStatuses userId={profile.user.user_id} />
          </TabsContent>

          {/* ── NEW: POSTS TAB ── */}
          <TabsContent value="posts">
            <UserPosts userId={profile.user.user_id} currentUser={currentUser} />
          </TabsContent>

          {/* ACTIVITY TIMELINE — preserved exactly from your original */}
          <TabsContent value="activity">
            {activity.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
                <Activity className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No activity yet.</p>
              </div>
            ) : (
              <div className="bg-card/30 rounded-2xl border border-white/5 p-6">
                {activity.map(a => <ActivityTimelineItem key={a.activity_id} activity={a} />)}
              </div>
            )}
          </TabsContent>

          {/* THREADS — preserved exactly from your original */}
          <TabsContent value="threads" className="space-y-4">
            {threads.length === 0 && (
              <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
                <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No threads created yet.</p>
              </div>
            )}
            {threads.map(thread => (
              <Link to={`/community/${thread.thread_id}`} key={thread.thread_id} className="block group">
                <div className="bg-card border border-white/5 p-6 rounded-xl group-hover:border-primary/40 transition-colors">
                  <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">{thread.title}</h3>
                  <p className="text-muted-foreground line-clamp-2 mb-3 text-sm">{thread.content}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="bg-primary/15 text-primary px-2 py-1 rounded-full font-medium">{thread.category}</span>
                    <span>{formatDistanceToNow(new Date(thread.created_at), { addSuffix: true })}</span>
                    <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {thread.comment_count}</span>
                  </div>
                </div>
              </Link>
            ))}
          </TabsContent>

          {/* COMMENTS — preserved exactly from your original */}
          <TabsContent value="comments" className="space-y-4">
            {comments.length === 0 && (
              <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
                <p className="text-muted-foreground">No comments yet.</p>
              </div>
            )}
            {comments.map(comment => (
              <Link to={`/movie/${comment.movie_id}`} key={comment.comment_id} className="block group">
                <div className="bg-card border border-white/5 p-5 rounded-xl group-hover:border-primary/40 transition-colors">
                  <p className="text-gray-200 mb-3 italic text-sm leading-relaxed">"{comment.content}"</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className={`font-semibold ${comment.score >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {comment.score >= 0 ? '+' : ''}{comment.score} pts
                    </span>
                    <span>·</span>
                    <span>{formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}</span>
                  </div>
                </div>
              </Link>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
